﻿import { api, request, PW_SERVER, SUBSCRIPTION_BASE } from "@/api/client";

const ACCESS_BASE = `${PW_SERVER}/accessmanager/subscription/v1`;

export interface SubscriptionPlan {
  plan_id?: string;
  product_id?: string;
  plan_code: string;
  name: string;
  status?: string;
  recurring_price?: number;
  interval?: number;
  billing_interval?: string;
  interval_unit?: string;
  currency_code?: string;
  store_markup_description?: string;
  features?: string[];
  feature_details?: { features?: Array<{ name?: string }> };
}

export interface AddonPermission {
  name?: string | null;
  addon_code: string;
  quantity?: number;
  usage_limit?: number;
}

export interface SubscriptionPermission {
  name: string;
  usageLimit: number | null;
}

export interface SubscriptionRecord {
  status?: string;
  planId?: string;
  planCode?: string;
  name?: string;
  planName?: string;
  currentTermStartsAt?: string;
  currentTermEndsAt?: string;
  trialEndsAt?: string;
  nextBillingAt?: string;
  cardNumber?: string;
  cardStatus?: string;
  permissions?: SubscriptionPermission[];
  addonPermissions?: AddonPermission[];
}

export interface SubscriptionTransaction {
  id: string | number;
  paymentDate?: string;
  payment_date?: string;
  planCode?: string;
  planName?: string;
  quantity?: number;
  amount?: number;
  currencySymbol?: string;
  isTrail?: boolean;
}

export interface SubscriptionAddon {
  addon_id?: string;
  addon_code: string;
  name: string;
  description?: string;
  type_formatted?: string;
  type?: string;
  interval_unit?: string;
  product_id?: string;
  applicable_to_all_plans?: boolean;
  pricing_scheme?: string;
  currency_code?: string;
  currency_symbol?: string;
  status?: string;
  price_brackets?: Array<{
    price?: number;
    start_quantity?: number;
    end_quantity?: number;
  }>;
  plans?: SubscriptionPlan[];
}

export interface PurchaseAddonItem {
  addonCode: string;
  purchaseType?: string;
  purchaseQuantity: number;
}

interface IncreaseAddonResponse {
  status: string;
  message?: string;
  requestId?: string;
  addonPermissions?: AddonPermission[];
}

export function buildPlanFeatures(plan: SubscriptionPlan): string[] {
  if (plan.features?.length) return plan.features;
  const featureNames = plan.feature_details?.features
    ?.map((feature) => feature.name?.trim())
    .filter((name): name is string => Boolean(name));
  if (featureNames?.length) return featureNames;
  const raw = plan.store_markup_description;
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw) as { features?: Array<{ name?: string }> };
    if (Array.isArray(parsed.features)) {
      const names = parsed.features.map((f) => f.name).filter(Boolean);
      if (names.length > 0) return names as string[];
    }
  } catch {
    // fall through to pipe-delimited parsing below
  }
  return raw
    .split("|")
    .map((part) => part.trim())
    .filter(Boolean);
}

export function billingIntervalLabel(plan: SubscriptionPlan): string {
  const unit = plan.interval_unit ?? plan.billing_interval;
  return unit ? unit.replace(/s$/, "") : "month";
}

export async function getSubscriptionPlans(): Promise<{
  plans: SubscriptionPlan[];
}> {
  return request("get", `${ACCESS_BASE}/plans`, { skipAuth: true });
}

export async function getSubscriptionPermissions(): Promise<SubscriptionRecord> {
  return api("get", `${SUBSCRIPTION_BASE}/subscription`);
}

export async function getSubscriptionTransactions(): Promise<
  SubscriptionTransaction[]
> {
  return api("get", `${SUBSCRIPTION_BASE}/transactions`);
}

export async function getAddons(): Promise<{ addons: SubscriptionAddon[] }> {
  return api("get", `${SUBSCRIPTION_BASE}/addons`);
}

export async function increaseAddon(payload: {
  purchaseAddonsList: PurchaseAddonItem[];
  requestId: string;
  quoteHash: string;
}): Promise<IncreaseAddonResponse> {
  return api("post", `${SUBSCRIPTION_BASE}/increase-addon`, payload);
}

export async function getPendingAddonPurchases(): Promise<
  Array<{ requestId: string; status: string; message?: string }>
> {
  return api("get", `${SUBSCRIPTION_BASE}/pending-addon-purchases`);
}

/** Reads a saved intent without submitting or confirming a payment. */
export async function getAddonPurchaseStatus(
  requestId: string,
): Promise<Array<{ requestId: string; status: string }>> {
  return api(
    "get",
    `${SUBSCRIPTION_BASE}/pending-addon-purchases?requestId=${encodeURIComponent(requestId)}`,
  );
}

export async function confirmAddonPurchase(
  requestId: string,
): Promise<IncreaseAddonResponse> {
  return api("post", `${SUBSCRIPTION_BASE}/confirm-addon-purchase`, {
    requestId,
  });
}

export async function paymentRequest(): Promise<{ url: string }> {
  return api("get", `${SUBSCRIPTION_BASE}/payment-request`);
}

export async function updateCardStatus(): Promise<void> {
  return api("post", `${SUBSCRIPTION_BASE}/card-status`, {});
}

// ── Additional functions migrated from develop ──

export async function ensureZohoCustomer(payload: {
  email: string;
  companyName: string;
  companyId: string | number;
  user: string | number;
  planId?: string;
  planCode?: string;
}): Promise<unknown> {
  return api("post", `${SUBSCRIPTION_BASE}/zoho-customer`, payload);
}

export interface BillingSummary {
  amount?: number;
  currency_code?: string;
  interval?: number;
  interval_unit?: string;
  status?: string;
  next_billing_at?: string;
  current_term_ends_at?: string;
  product_id?: string;
  plan?: BillingRateItem;
  addons?: Array<BillingRateItem & { addon_code: string; quantity: number }>;
  scheduled_state?: "none" | "scheduled" | "unavailable";
  scheduled_changes?: BillingSummary;
  scheduled_effective_at?: string;
  checked_at?: string;
  capacity_review?: AddonReductionImpact[];
  payment_reviews?: Array<{ invoice_id: string; message: string }>;
  price_history?: Array<{
    id: number;
    observed_at: string;
    previous: { current?: BillingSummary; scheduled?: BillingSummary };
    current: BillingSummary & { scheduled?: BillingSummary };
  }>;
}
export interface BillingRateItem {
  addon_code?: string;
  plan_code?: string;
  name?: string;
  quantity?: number;
  price?: number;
  total?: number;
  discount?: number;
  pricing_source?: "custom" | "catalog" | "price_list" | "subscription";
}
export interface AddonPurchaseQuote {
  quote_hash: string;
  currency_code: string;
  interval?: number;
  interval_unit?: string;
  additional_recurring_amount: number | null;
  one_time_amount: number | null;
  charge_note: string;
  blocked_reason?: string;
  items: Array<{
    addon_code: string;
    name: string;
    additional_quantity: number;
    current_quantity: number;
    unit_price?: number;
    amount?: number;
    recurring: boolean;
    pricing_source: string;
    pricing_scheme?: string;
    pack_size?: number;
  }>;
}
export const previewAddonPurchase = (
  purchaseAddonsList: PurchaseAddonItem[],
): Promise<AddonPurchaseQuote> =>
  api("post", `${SUBSCRIPTION_BASE}/preview-addon-purchase`, {
    purchaseAddonsList,
  });
export const getBillingSummary = (): Promise<BillingSummary> =>
  api("get", `${SUBSCRIPTION_BASE}/billing-summary`);
export const cancelSubscription = (): Promise<unknown> =>
  api("post", `${SUBSCRIPTION_BASE}/cancel-subscription`, {});
export const reactivateSubscription = (): Promise<unknown> =>
  api("post", `${SUBSCRIPTION_BASE}/reactivate-subscription`, {});
export const decreaseAddon = (
  addonCode: string,
  quantity: number,
  disable: AddonReductionSelection = {},
): Promise<unknown> =>
  api("post", `${SUBSCRIPTION_BASE}/decrease-addon`, {
    addonCode,
    quantity,
    ...(Object.values(disable).some((ids) => ids?.length) ? { disable } : {}),
  });

export type CapacityFeature = "USER" | "FACILITY" | "SUPPLIER";
export type AddonReductionSelection = Partial<
  Record<CapacityFeature, number[]>
>;
export interface AddonReductionImpact {
  feature: CapacityFeature;
  label: string;
  active: number;
  limit: number | null;
  required: number;
}
export interface AddonReductionRecord {
  id: number;
  name: string;
  detail: string;
  selectable: boolean;
}
export const previewAddonReduction = (
  addonCode: string,
  quantity: number,
): Promise<{ resources: AddonReductionImpact[] }> =>
  api("post", `${SUBSCRIPTION_BASE}/preview-addon-reduction`, {
    addonCode,
    quantity,
  });
export const getBillingCapacityReview = (): Promise<{
  resources: AddonReductionImpact[];
}> => api("get", `${SUBSCRIPTION_BASE}/billing-capacity-review`);
export const resolveBillingCapacity = (
  disable: AddonReductionSelection,
): Promise<{ resources: AddonReductionImpact[] }> =>
  api("post", `${SUBSCRIPTION_BASE}/resolve-billing-capacity`, { disable });

export function billingMoney(amount: unknown, currency?: string): string {
  if (
    amount == null ||
    !currency ||
    !Number.isFinite(Number(amount)) ||
    Number(amount) < 0
  )
    return "Price unavailable";
  try {
    return new Intl.NumberFormat(undefined, {
      style: "currency",
      currency,
    }).format(Number(amount));
  } catch {
    return "Price unavailable";
  }
}
export const getAddonReductionRecords = (
  feature: CapacityFeature,
  page: number,
  search: string,
): Promise<{ records: AddonReductionRecord[]; total: number }> =>
  api("get", `${SUBSCRIPTION_BASE}/addon-reduction-records`, undefined, {
    feature,
    page,
    size: 25,
    search,
  });

export function contractPrice(summary?: BillingSummary): string {
  if (
    summary?.amount == null ||
    !summary.currency_code ||
    !summary.interval ||
    !summary.interval_unit
  )
    return "Billing amount unavailable";
  const amount = Number(summary.amount);
  if (!Number.isFinite(amount) || amount < 0)
    return "Billing amount unavailable";
  try {
    return `${new Intl.NumberFormat(undefined, { style: "currency", currency: summary.currency_code }).format(amount)} / ${summary.interval} ${summary.interval === 1 ? summary.interval_unit.replace(/s$/, "") : summary.interval_unit}`;
  } catch {
    return "Billing amount unavailable";
  }
}
export function renewalLabel(status?: string): string {
  if (status === "non_renewing")
    return "Ends at the current term — renewal stopped";
  if (["live", "active", "trial", "trialing"].includes(status ?? ""))
    return "Auto renewing";
  if (["cancelled", "expired", "trial_expired"].includes(status ?? ""))
    return "Subscription ended";
  return "Renewal status unavailable";
}

export async function createZohoSubscription(
  subscription: Record<string, unknown>,
): Promise<unknown> {
  return api("post", `${SUBSCRIPTION_BASE}/zoho-subscription`, subscription);
}

export async function upgradeSubscription(
  upgradeRequest: Record<string, unknown>,
): Promise<unknown> {
  return api(
    "post",
    `${SUBSCRIPTION_BASE}/upgrade-subscription`,
    upgradeRequest,
  );
}
